import React from "react";

import { shortDate } from "./shared";
import type { PlanViewState } from "../../Types/AccountPlan";

/**
 * Sponsors always read the newest plan version (Q1 = C + A, Sushant 2026-10-19),
 * so every sponsor surface says whether that version is still a draft or has
 * been published by the account team. One label, two sizes: a compact pill for
 * lists and Quick look, a banner line for Briefing, Briefing Questions and View source.
 */

export const DRAFT_HINT = "not yet reviewed by the account team";

/** The label's state from a version's publish record (null/undefined = draft). */
export const viewStateOf = (
  published?: { at?: string | null } | null
): PlanViewState => (published ? "published" : "draft");

const PILL =
  "inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider";
const TONE: Record<PlanViewState, string> = {
  draft: "bg-amber-200 text-amber-900",
  published: "bg-emerald-100 text-emerald-800",
};

interface Props {
  state: PlanViewState;
  /** When the version was published; shown as "Published <date>". */
  publishedAt?: string | null;
  /** pill: "Draft" / "Published <date>"; banner adds the draft explanation. */
  variant?: "pill" | "banner";
  /** Leave the date off a published pill when the line beside it already has it. */
  showDate?: boolean;
  className?: string;
}

const PlanStateLabel: React.FC<Props> = ({
  state,
  publishedAt,
  variant = "pill",
  showDate = true,
  className = "",
}) => {
  const draft = state === "draft";
  const text = draft
    ? "Draft"
    : showDate && publishedAt
      ? `Published ${shortDate(publishedAt)}`
      : "Published";
  return (
    <span
      data-testid="plan-state-label"
      data-state={state}
      title={
        draft
          ? `Draft — ${DRAFT_HINT}`
          : `Published${publishedAt ? ` ${shortDate(publishedAt)}` : ""} by the account team`
      }
      className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}
    >
      <span className={`${PILL} ${TONE[state]}`}>{text}</span>
      {variant === "banner" && draft && (
        <>
          {" "}
          <span className="text-sm text-amber-900">— {DRAFT_HINT}</span>
        </>
      )}
    </span>
  );
};

export default PlanStateLabel;
